import { apiClient } from './api-client'

export interface AISettings {
  enableAutoAnalysis: boolean
  confidenceThreshold: number
  enableSmartFiltering: boolean
  autoClassification: boolean
}

export interface DJENSettings {
  syncInterval: number
  enableAutoSync: boolean
  maxRetryAttempts: number
  timeoutSeconds: number
}

export interface RolePermissions {
  [role: string]: {
    canViewPublications: boolean
    canEditPublications: boolean
    canDeletePublications: boolean
    canViewUsers: boolean
    canEditUsers: boolean
    canDeleteUsers: boolean
    canViewClients: boolean
    canEditClients: boolean
    canDeleteClients: boolean
    canViewCases: boolean
    canEditCases: boolean
    canDeleteCases: boolean
    canViewFinance: boolean
    canEditFinance: boolean
    canDeleteFinance: boolean
    canViewReports: boolean
    canViewSettings: boolean
    canEditSettings: boolean
  }
}

export interface SecuritySettings {
  passwordMinLength: number
  passwordRequireSpecialChars: boolean
  passwordRequireNumbers: boolean
  passwordRequireUppercase: boolean
  sessionTimeoutMinutes: number
  maxLoginAttempts: number
  lockoutDurationMinutes: number
}

export interface NotificationSettings {
  emailNotifications: boolean
  newPublicationAlert: boolean
  deadlineReminders: boolean
  systemMaintenanceAlerts: boolean
}

export interface SystemSettings {
  ai: AISettings
  djen: DJENSettings
  permissions: RolePermissions
  security: SecuritySettings
  notifications: NotificationSettings
}

class SettingsService {
  private readonly baseUrl = '/api/settings'

  async getSettings(): Promise<SystemSettings> {
    try {
      const response = await apiClient.get<{ success: boolean; data: SystemSettings }>(this.baseUrl)
      return response.data.data
    } catch (error) {
      console.error('Error fetching settings:', error)
      return this.getDefaultSettings()
    }
  }

  async updateAISettings(settings: AISettings): Promise<void> {
    await apiClient.put(`${this.baseUrl}/ai`, settings)
  }

  async updateDJENSettings(settings: DJENSettings): Promise<void> {
    await apiClient.put(`${this.baseUrl}/djen`, settings)
  }

  async updatePermissions(permissions: RolePermissions): Promise<void> {
    await apiClient.put(`${this.baseUrl}/permissions`, permissions)
  }

  async updateSecuritySettings(settings: SecuritySettings): Promise<void> {
    await apiClient.put(`${this.baseUrl}/security`, settings)
  }

  async updateNotificationSettings(settings: NotificationSettings): Promise<void> {
    await apiClient.put(`${this.baseUrl}/notifications`, settings)
  }

  private getDefaultSettings(): SystemSettings {
    return {
      ai: {
        enableAutoAnalysis: true,
        confidenceThreshold: 0.8,
        enableSmartFiltering: true,
        autoClassification: false
      },
      djen: {
        syncInterval: 240, // 4 hours default
        enableAutoSync: true,
        maxRetryAttempts: 3,
        timeoutSeconds: 30
      },
      permissions: {
        SUPER_ADMIN: {
          canViewPublications: true,
          canEditPublications: true,
          canDeletePublications: true,
          canViewUsers: true,
          canEditUsers: true,
          canDeleteUsers: true,
          canViewClients: true,
          canEditClients: true,
          canDeleteClients: true,
          canViewCases: true,
          canEditCases: true,
          canDeleteCases: true,
          canViewFinance: true,
          canEditFinance: true,
          canDeleteFinance: true,
          canViewReports: true,
          canViewSettings: true,
          canEditSettings: true
        },
        ADMIN: {
          canViewPublications: true,
          canEditPublications: true,
          canDeletePublications: false,
          canViewUsers: true,
          canEditUsers: true,
          canDeleteUsers: false,
          canViewClients: true,
          canEditClients: true,
          canDeleteClients: false,
          canViewCases: true,
          canEditCases: true,
          canDeleteCases: false,
          canViewFinance: true,
          canEditFinance: true,
          canDeleteFinance: false,
          canViewReports: true,
          canViewSettings: true,
          canEditSettings: false
        },
        LAWYER: {
          canViewPublications: true,
          canEditPublications: true,
          canDeletePublications: false,
          canViewUsers: false,
          canEditUsers: false,
          canDeleteUsers: false,
          canViewClients: true,
          canEditClients: true,
          canDeleteClients: false,
          canViewCases: true,
          canEditCases: true,
          canDeleteCases: false,
          canViewFinance: true,
          canEditFinance: false,
          canDeleteFinance: false,
          canViewReports: true,
          canViewSettings: false,
          canEditSettings: false
        },
        ASSISTANT: {
          canViewPublications: true,
          canEditPublications: false,
          canDeletePublications: false,
          canViewUsers: false,
          canEditUsers: false,
          canDeleteUsers: false,
          canViewClients: true,
          canEditClients: true,
          canDeleteClients: false,
          canViewCases: true,
          canEditCases: true,
          canDeleteCases: false,
          canViewFinance: true,
          canEditFinance: false,
          canDeleteFinance: false,
          canViewReports: false,
          canViewSettings: false,
          canEditSettings: false
        },
        CLIENT: {
          canViewPublications: false,
          canEditPublications: false,
          canDeletePublications: false,
          canViewUsers: false,
          canEditUsers: false,
          canDeleteUsers: false,
          canViewClients: false,
          canEditClients: false,
          canDeleteClients: false,
          canViewCases: true,
          canEditCases: false,
          canDeleteCases: false,
          canViewFinance: false,
          canEditFinance: false,
          canDeleteFinance: false,
          canViewReports: false,
          canViewSettings: false,
          canEditSettings: false
        },
        USER: {
          canViewPublications: false,
          canEditPublications: false,
          canDeletePublications: false,
          canViewUsers: false,
          canEditUsers: false,
          canDeleteUsers: false,
          canViewClients: true,
          canEditClients: false,
          canDeleteClients: false,
          canViewCases: true,
          canEditCases: false,
          canDeleteCases: false,
          canViewFinance: false,
          canEditFinance: false,
          canDeleteFinance: false,
          canViewReports: false,
          canViewSettings: false,
          canEditSettings: false
        }
      },
      security: {
        passwordMinLength: 8,
        passwordRequireSpecialChars: true,
        passwordRequireNumbers: true,
        passwordRequireUppercase: true,
        sessionTimeoutMinutes: 60,
        maxLoginAttempts: 5,
        lockoutDurationMinutes: 15
      },
      notifications: {
        emailNotifications: true,
        newPublicationAlert: true,
        deadlineReminders: true,
        systemMaintenanceAlerts: true
      }
    }
  }
}

export const settingsService = new SettingsService()
